import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";
import { Plus, UserCog, Trash2 } from "lucide-react";
import { toast } from "sonner";

const ROLE_LABEL = { tenant_admin: "管理者", nurse: "看護師/療法士", office: "事務" };
const QUALIFICATIONS = ["看護師", "准看護師", "理学療法士", "作業療法士", "言語聴覚士", "事務", "保健師", "助産師"];

export default function Staff() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    email: "", password: "", name: "", role: "nurse",
    qualifications: [], license_number: "", phone: "",
  });

  const load = () => api.get("/users").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.email || !form.password || !form.name) { toast.error("必須項目を入力してください"); return; }
    try {
      await api.post("/users", form);
      toast.success("スタッフを登録しました");
      setOpen(false);
      setForm({ email: "", password: "", name: "", role: "nurse", qualifications: [], license_number: "", phone: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "登録失敗");
    }
  };

  const del = async (id) => {
    if (!confirm("削除しますか?")) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success("削除しました");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "削除失敗");
    }
  };

  const toggleQual = (q) => {
    const has = form.qualifications.includes(q);
    setForm({ ...form, qualifications: has ? form.qualifications.filter((x) => x !== q) : [...form.qualifications, q] });
  };

  return (
    <div className="space-y-6" data-testid="staff-page">
      <div className="flex justify-between items-end flex-wrap gap-4">
        <div>
          <div className="text-xs tracking-[0.2em] uppercase text-[#8A8A86] font-numeric mb-2">STAFF</div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">スタッフ管理</h1>
          <p className="text-[#4A4A48] mt-1">{items.length}名</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-staff-button" className="bg-[#2C5545] text-white"><Plus size={16} className="mr-2" />新規スタッフ</Button>
          </DialogTrigger>
          <DialogContent className="max-w-xl">
            <DialogHeader><DialogTitle>スタッフ登録</DialogTitle></DialogHeader>
            <div className="space-y-3 py-3">
              <div><Label>氏名<span className="text-[#A74141]">*</span></Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>メール<span className="text-[#A74141]">*</span></Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                <div><Label>初期パスワード<span className="text-[#A74141]">*</span></Label><Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
              </div>
              <div>
                <Label>ロール</Label>
                <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="flex h-10 w-full rounded-md border border-input px-3 text-sm">
                  <option value="tenant_admin">管理者</option>
                  <option value="nurse">看護師/療法士</option>
                  <option value="office">事務</option>
                </select>
              </div>
              <div>
                <Label>資格 (複数可)</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {QUALIFICATIONS.map((q) => (
                    <button
                      type="button"
                      key={q}
                      onClick={() => toggleQual(q)}
                      className={`px-3 py-1 text-sm rounded-full border transition ${
                        form.qualifications.includes(q)
                          ? "bg-[#2C5545] text-white border-[#2C5545]"
                          : "bg-white text-[#4A4A48] border-[#E5E5E3] hover:bg-[#F3F3F1]"
                      }`}
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>免許番号</Label><Input value={form.license_number} onChange={(e) => setForm({ ...form, license_number: e.target.value })} /></div>
                <div><Label>電話</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>キャンセル</Button>
              <Button data-testid="save-staff-button" className="bg-[#2C5545] text-white" onClick={save}>登録</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border-[#E5E5E3] bg-white">
        <div className="divide-y divide-[#E5E5E3]">
          {items.map((s) => (
            <div key={s.id} className="p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-[#D9E2D5] flex items-center justify-center">
                <UserCog size={18} className="text-[#2C5545]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium">{s.name}</div>
                <div className="text-sm text-[#8A8A86] truncate font-numeric">{s.email}</div>
                {s.qualifications?.length > 0 && (
                  <div className="flex gap-1 mt-1 flex-wrap">
                    {s.qualifications.map((q) => <Badge key={q} variant="outline" className="text-[10px]">{q}</Badge>)}
                  </div>
                )}
              </div>
              <Badge variant="outline" className="border-[#D9E2D5] text-[#2C5545]">{ROLE_LABEL[s.role]}</Badge>
              <button onClick={() => del(s.id)} className="text-[#A74141] hover:text-[#8B2E2E]"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
